import { Button, pantryColors, pantryTypography } from '@pantry/ui'
import { Formik } from 'formik'
import React from 'react'
import * as Yup from 'yup'
import type { TagColorOption, TagFormValues } from '../../../../types/tag'

const p = pantryColors
const F = pantryTypography.fontFamily

interface AddTagDialogProps {
  open: boolean
  colorOptions: TagColorOption[]
  isSaving: boolean
  onClose: () => void
  onCreate: (values: TagFormValues) => void
}

const schema = Yup.object({
  name: Yup.string().trim().required('Please enter a Tag Name'),
})

const initialValues: TagFormValues = {
  name: '',
  slug: '',
  description: '',
  visible: true,
  colorId: 'brand',
  style: 'filled',
  // Always defaults to unlocked on quick create — the user can lock it later
  // from the full drawer if they need to pin the slug independently from the
  // name. Sent through to the backend as `item_data.slug_locked: false`.
  slugLocked: false,
}

export const AddTagDialog: React.FC<AddTagDialogProps> = ({ open, colorOptions, isSaving, onClose, onCreate }) => {
  if (!open) return null

  return (
    <Formik initialValues={initialValues} validationSchema={schema} onSubmit={(values) => onCreate(values)}>
      {({ values, errors, touched, setFieldValue, handleChange, submitForm }) => {
        const selectedColor = colorOptions.find((o) => o.id === values.colorId) ?? colorOptions[0]
        const showNameError = touched.name && !!errors.name

        return (
          <div style={overlayStyle} onClick={onClose}>
            <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
              <div style={iconWrapStyle}>
                <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8" width="22" height="22">
                  <path d="M11 4v14M4 11h14" />
                </svg>
              </div>
              <div style={titleStyle}>New Tag</div>

              <div style={fieldWrapStyle}>
                <label htmlFor="quick-tag-name" style={labelStyle}>Tag Name</label>
                <input
                  id="quick-tag-name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  placeholder="e.g. Staff Pick"
                  style={inputStyle}
                />
                {showNameError ? <div style={errorStyle}>{errors.name}</div> : null}
              </div>

              <div style={previewWrapStyle}>
                <span style={previewLabelStyle}>Preview:</span>
                <span style={{ ...previewBadgeStyle, background: selectedColor.backgroundColor, color: selectedColor.textColor }}>
                  {values.name.trim() || 'Staff Pick'}
                </span>
              </div>

              <div style={fieldWrapStyle}>
                <div style={labelStyle}>Colour</div>
                <div style={colorGridStyle}>
                  {colorOptions.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      aria-label={option.label}
                      onClick={() => setFieldValue('colorId', option.id)}
                      style={{
                        ...colorSwatchStyle,
                        background: option.backgroundColor,
                        outline: `1.5px solid ${option.borderColor}`,
                        borderColor: values.colorId === option.id ? p.gray[700] : 'transparent',
                        transform: values.colorId === option.id ? 'scale(1.08)' : 'scale(1)',
                      }}
                    >
                      {values.colorId === option.id ? <span style={swatchTickStyle}>✓</span> : null}
                    </button>
                  ))}
                </div>
              </div>

              <div style={footerStyle}>
                <Button variant="ghost" onClick={onClose} disabled={isSaving}>Cancel</Button>
                <Button variant="primary" onClick={submitForm} disabled={isSaving}>Create Tag</Button>
              </div>
            </div>
          </div>
        )
      }}
    </Formik>
  )
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 80,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12,
}
const modalStyle: React.CSSProperties = {
  background: p.white, borderRadius: 16, padding: 28, width: 370, maxWidth: '94vw',
  boxShadow: '0 24px 56px rgba(16,24,40,.2)', fontFamily: F,
}
const iconWrapStyle: React.CSSProperties = {
  width: 44, height: 44, borderRadius: 10, background: p.brand.light, color: p.brand.primary,
  display: 'grid', placeItems: 'center', marginBottom: 14,
}
const titleStyle: React.CSSProperties = { fontSize: 17, fontWeight: 700, color: p.gray[800], marginBottom: 10 }
const fieldWrapStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }
const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 500, color: p.gray[600] }
const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '8px 11px',
  border: `1px solid ${p.gray[300]}`, borderRadius: 6, fontSize: 13.5, color: p.gray[800], outline: 'none', fontFamily: F,
}
const previewWrapStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }
const previewLabelStyle: React.CSSProperties = { fontSize: 11.5, color: p.gray[400] }
const previewBadgeStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', borderRadius: 999, padding: '5px 12px',
  fontSize: 13, fontWeight: 600, letterSpacing: '0.01em',
}
const colorGridStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(6, 28px)', gap: 6, margin: '8px 0 2px',
}
const colorSwatchStyle: React.CSSProperties = {
  width: 28, height: 28, borderRadius: 6, borderWidth: 2.5, borderStyle: 'solid',
  cursor: 'pointer', transition: 'transform .12s, border-color .12s', position: 'relative',
}
const swatchTickStyle: React.CSSProperties = {
  position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
  color: p.white, fontSize: 11, fontWeight: 700, textShadow: '0 1px 2px rgba(0,0,0,.3)',
}
const footerStyle: React.CSSProperties = { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }
const errorStyle: React.CSSProperties = { color: p.red[700], fontSize: 12 }
